import { PowerBIEmbed } from "powerbi-client-react";
import React, { useEffect, useRef, useState } from "react";
import * as models from "powerbi-models";
import * as powerbiClient from "powerbi-client";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Tooltip } from "antd";
import { LogoutOutlined } from "@ant-design/icons"; // added icon
import {
  getStoredEmbedData,
  clearSession,
  isTokenExpired,
  useSessionTimeout,
  hasExpiredFlag,
  EXPIRED_FLAG_KEY,
} from "../utils/session";
import "../styles/session-timeout.scss";
import "../styles/dashboard.scss";
import CustomDialog from "../components/CustomDialog";

interface EmbedDataShape {
  token?: string;
  embedUrl?: string;
  reportId?: string;
  [k: string]: any;
}

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locData = location.state as any;
  const initial = (locData?.apiData as EmbedDataShape) || getStoredEmbedData();
  const [embedData] = useState<EmbedDataShape | null>(initial);
  const loadedPromiseRef = useRef<{ resolve?: () => void }>({});
  const renderedPromiseRef = useRef<{ resolve?: () => void }>({});
  const [logoutOpen, setLogoutOpen] = useState(false); // logout confirmation modal state
  const [forceExpired, setForceExpired] = useState(false); // force modal when embed returns 401/403 without clearing session
  const [initialExpiredFlag] = useState<boolean>(() => hasExpiredFlag()); // capture expired flag at mount (page refresh scenario)

  const reportLoaded = new Promise<void>(
    (res) => (loadedPromiseRef.current.resolve = res)
  );
  const reportRendered = new Promise<void>(
    (res) => (renderedPromiseRef.current.resolve = res)
  );

  useEffect(() => {
    document.title = "Dashboard";
    reportLoaded.then(() => {
      // report loaded
    });
    reportRendered.then(() => {
      // report rendered
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { expired } = useSessionTimeout(embedData);
  const showExpired =
    !initialExpiredFlag && // if page loaded with expired flag, skip showing modal (auto redirect)
    (forceExpired ||
      expired ||
      (embedData && isTokenExpired(embedData)) ||
      hasExpiredFlag());

  const reportRef = useRef<powerbiClient.Report | null>(null);

  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  useEffect(() => {
    if (!embedData || isTokenExpired(embedData)) {
      return; // showExpired effect will handle redirect
    }
  }, [embedData]);

  useEffect(() => {
    if (!embedData) {
      navigate("/", { replace: true });
    }
  }, [embedData, navigate]);

  useEffect(() => {
    if (initialExpiredFlag) {
      // Session already marked expired on page load (likely refresh); go straight to login.
      clearSession();
      navigate("/", { replace: true });
    }
  }, [initialExpiredFlag, navigate]);

  if (!embedData) return null;

  const accessToken = embedData.token!;
  const embedUrl = embedData.embedUrl!;
  const reportId = embedData.reportId!;
  const tokenType = models.TokenType.Embed;

  const embedConfig: models.IReportEmbedConfiguration = {
    type: "report",
    id: reportId,
    embedUrl: embedUrl,
    accessToken: accessToken,
    tokenType: tokenType,
    permissions: models.Permissions.All,
    settings: {
      panes: {
        filters: { visible: false },
        pageNavigation: { visible: true },
      },
    },
  };

  const timeoutModal = (
    <CustomDialog
      open={showExpired}
      title="Session Timeout"
      okText="Login"
      onOk={() => {
        clearSession();
        navigate("/", { replace: true });
      }}
      buttonSize="middle"
      className="session-timeout-modal"
      maskBackground="rgba(0,0,0,0.5)"
      getContainer={false}
      description="Your session has expired. Please log in again."
    />
  );

  const logoutModal = (
    <CustomDialog
      open={logoutOpen && !showExpired}
      title="Logout"
      okText="Yes"
      cancelText="No"
      buttonSize="middle"
      onCancel={() => setLogoutOpen(false)}
      onOk={() => {
        clearSession();
        navigate("/", { replace: true });
      }}
      description="Are you sure you want to logout?"
    />
  );

  const handleEmbedError = (
    event?: powerbiClient.service.ICustomEvent<any>
  ) => {
    const detail: any = event?.detail;
    const status = detail?.httpStatusCode || detail?.status;
    const isAuthErr =
      status === 401 ||
      status === 403 ||
      (typeof detail?.message === "string" &&
        /(401|403)/.test(detail.message)) ||
      /(401|403)/.test(JSON.stringify(detail || {}));
    if (isAuthErr) {
      // Do NOT clear session immediately. Show timeout modal so user can acknowledge.
      try {
        localStorage.setItem(EXPIRED_FLAG_KEY, "1");
      } catch {}
      setForceExpired(true);
      return;
    }
  };

  return (
    <div
      className={`dashboard-wrapper ${
        showExpired ? "session-timeout-active" : ""
      }`}
    >
      {!showExpired && (
        <Tooltip title="Logout">
          <Button
            shape="circle"
            icon={<LogoutOutlined />}
            aria-label="Logout"
            onClick={() => setLogoutOpen(true)}
            className="logout-fab"
            size="large"
          />
        </Tooltip>
      )}
      <PowerBIEmbed
        embedConfig={embedConfig}
        eventHandlers={
          new Map<
            string,
            (event?: powerbiClient.service.ICustomEvent<any>) => void
          >([
            [
              "loaded",
              () => {
                loadedPromiseRef.current.resolve?.();
              },
            ],
            [
              "rendered",
              () => {
                renderedPromiseRef.current.resolve?.();
              },
            ],
            ["error", (event) => handleEmbedError(event)],
          ])
        }
        getEmbeddedComponent={(embeddedReport) => {
          reportRef.current = embeddedReport as powerbiClient.Report;
        }}
        cssClassName="reportClass"
      />
      {timeoutModal}
      {logoutModal}
    </div>
  );
};

export default Dashboard;
